import { useState, useEffect, useRef, useCallback, lazy, Suspense } from "react";
import { paymentMethods, cryptoCurrencies, calculateCryptoAmount } from "./data.js";
import { clearAuthToken, getAuthToken, setAuthToken, setRefreshToken, waitForInit, logoutCurrentSession } from "../../graphql/client.js";
import { secureGet, secureSet } from "../../utils/secureStorage.js";
import { useDisconnect } from "wagmi";
import { GET_USER_KYC_LIMITS, GET_ACTIVE_CURRENCIES, LOGOUT } from "../../graphql/auth.js";
import { useQuery, useMutation } from "@apollo/client/react";
import { connectChatSocket, getChatSocket, disconnectChatSocket } from "../../lib/chatSocket";
import { connectPresenceSocket, disconnectPresenceSocket } from "../../lib/presenceSocket";
import { jwtDecode } from "jwt-decode";
import { useTranslation } from "@/lib/i18n.jsx";

// Critical — rendered on initial load
import { MainForm } from "./MainForm.jsx";
import { AuthForm } from "./AuthForm.jsx";
import { TrustBadge } from "./TrustBadge.jsx";

// Non-critical — lazy loaded only when user navigates to that step
const WalletEntry = lazy(() => import("./WalletEntry.jsx").then(m => ({ default: m.WalletEntry })));
const KYCVerification = lazy(() => import("./KYCVerification.jsx").then(m => ({ default: m.KYCVerification })));
const MerchantMatching = lazy(() => import("./MerchantMatching.jsx").then(m => ({ default: m.MerchantMatching })));
const PaymentInstructions = lazy(() => import("./PaymentInstructions.jsx").then(m => ({ default: m.PaymentInstructions })));
const TransactionStatus = lazy(() => import("./TransactionStatus.jsx").then(m => ({ default: m.TransactionStatus })));
const ProfileScreen = lazy(() => import("./ProfileScreen.jsx").then(m => ({ default: m.ProfileScreen })));
const LimitsScreen = lazy(() => import("./LimitsScreen.jsx").then(m => ({ default: m.LimitsScreen })));
const OrdersScreen = lazy(() => import("./OrdersScreen.jsx").then(m => ({ default: m.OrdersScreen })));
const PaymentDetailsForm = lazy(() => import("./PaymentDetailsForm.jsx").then(m => ({ default: m.PaymentDetailsForm })));
const ChatList = lazy(() => import("./ChatList.jsx").then(m => ({ default: m.ChatList })));
const WidgetChat = lazy(() => import("./chat/WidgetChat.jsx").then(m => ({ default: m.WidgetChat })));
const SecurityScreen = lazy(() => import("./SecurityScreen.jsx").then(m => ({ default: m.SecurityScreen })));

const STORAGE_KEY = "crypto_widget_state";

export const CryptoWidget = () => {
  const { t } = useTranslation();
  // const [showSplash, setShowSplash] = useState(true);
  const [isInitialized, setIsInitialized] = useState(false);
  const [isDark, setIsDark] = useState(true);
  const [step, setStep] = useState("main");
  const [mode, setMode] = useState("buy");
  const [fiatAmount, setFiatAmount] = useState("10");
  const [cryptoAmount, setCryptoAmount] = useState("");
  const [selectedCurrency, setSelectedCurrency] = useState({
    code: "USD",
    symbol: "$",
    flag: "🇺🇸",
    countryCode: "US",
    name: "US Dollar",
  }); // placeholder until the admin-enabled currency list loads
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState(paymentMethods[0]);
  const [selectedCrypto, setSelectedCrypto] = useState(cryptoCurrencies[0]);
  const [merchant, setMerchant] = useState(null);
  const [walletAddress, setWalletAddress] = useState("");
  const [txHash, setTxHash] = useState(null);
  const [dealId, setDealId] = useState(null);
  const [pendingOrder, setPendingOrder] = useState(null);
  const [paymentDetails, setPaymentDetails] = useState(null);
  const [chatMerchant, setChatMerchant] = useState(null);
  const { disconnect } = useDisconnect();
  const [user, setUser] = useState({
    email: "",
    isLoggedIn: false,
    isKycComplete: false,
    walletAddress: "",
  });

  const [requireUserVideoKyc, setRequireUserVideoKyc] = useState(false);
  const [escrowTimeout, setEscrowTimeout] = useState(false);
  const [isStagingPartner, setIsStagingPartner] = useState(false);

  const {
    data: userKycData,
    loading: userKycLoading,
    refetch: refetchUserKyc,
    error: userKycError,
  } = useQuery(GET_USER_KYC_LIMITS, {
    skip: !user?.isLoggedIn, // no userId needed
    fetchPolicy: "network-only",
  });

  const { data: currenciesData } = useQuery(GET_ACTIVE_CURRENCIES);
  const currencyList = currenciesData?.getActiveCurrencies?.data || [];
  const pendingCurrencyCodeRef = useRef(null);
  const currenciesAppliedRef = useRef(false);

  // Apply the restored (or default) currency once the admin-enabled list arrives —
  // the list is fetched async, so this can't happen inline in the init effect below.
  useEffect(() => {
    if (currenciesAppliedRef.current || currencyList.length === 0) return;
    currenciesAppliedRef.current = true;

    const match = currencyList.find((c) => c.code === pendingCurrencyCodeRef.current);
    setSelectedCurrency(match || currencyList[0]);
  }, [currencyList]);

  const [logoutMutation] = useMutation(LOGOUT);

  const handleSessionExpired = useCallback(() => {
    clearAuthToken();
    disconnect();

    setUser({
      email: "",
      isLoggedIn: false,
      isKycComplete: false,
      walletAddress: "",
      dailyBuyLimit: 0,
      dailySellLimit: 0,
      escrowTimeout: 0,
    });

    setMerchant(null);
    setWalletAddress("");
    setTxHash(null);
    setDealId(null);
    setPaymentDetails(null);

    localStorage.removeItem(STORAGE_KEY);

    setStep("main");
  }, [disconnect]);

  // Global auth error handler — catches "User not found" / 401 from ANY query
  useEffect(() => {
    window.addEventListener("auth:session-expired", handleSessionExpired);
    return () => window.removeEventListener("auth:session-expired", handleSessionExpired);
  }, [handleSessionExpired]);

  useEffect(() => {
    if (user?.isLoggedIn) {
      const token = getAuthToken();
      if (token) {
        connectPresenceSocket(token, handleSessionExpired);
        // Pre-connect chat socket so it's ready before the user navigates to chat
        try {
          const decoded = jwtDecode(token);
          const chatUserId = Number(decoded?.userId);
          if (chatUserId) connectChatSocket(chatUserId, "USER");
        } catch { }
      }
    } else {
      disconnectPresenceSocket();
      disconnectChatSocket();
    }
  }, [user?.isLoggedIn, handleSessionExpired]);

  useEffect(() => {
    setPendingOrder(null);
    setStep("main");
    if (window.APP_CONFIG?.requireUserVideoKyc) {
      setRequireUserVideoKyc(true);
    }

    if (window.APP_CONFIG?.escrowTimeout) {
      setEscrowTimeout(BigInt(window.APP_CONFIG?.escrowTimeout) || 60);
    }

    if (window.APP_CONFIG?.isStagingPartner) {
      setIsStagingPartner(true);
    }
  }, []);

  useEffect(() => {
    const apiData = userKycData?.getUserKycAndLimits?.data;

    if (apiData) {
      setUser((prev) => ({
        ...prev,
        ...apiData,
        isLoggedIn: true,

        kycStatus: apiData?.kycStatus || null,
        isApproved: apiData?.isApproved || false,
        kycCompleted: apiData?.kycCompleted || false,
        submittedKyc: apiData?.submittedKyc || false,

        isKycComplete: apiData?.kycCompleted,
        adminPub: apiData?.adminPub
      }));

      setEscrowTimeout(apiData?.escrowTimeout || 0);
      setRequireUserVideoKyc(apiData?.requireUserVideoKyc || false);
    }
  }, [userKycData]);

  useEffect(() => {
    if (step === "main" && user?.isLoggedIn) {
      refetchUserKyc();
    }
  }, [step]);

  useEffect(() => {
    window.onWidgetChatOpen = (m) => {
      setChatMerchant(m);
    };
    return () => { delete window.onWidgetChatOpen; };
  }, []);

  useEffect(() => {
    const API_BASE = import.meta.env.VITE_API_BASE_URL || "https://api-p2p-stage.palzea.com";

    const handleSsoMessage = async (event) => {
      if (event.data?.type !== "SSO_LOGIN" || !event.data?.token) return;

      try {
        const res = await fetch(`${API_BASE}/auth/sso`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ token: event.data.token }),
        });

        const data = await res.json();
        if (!res.ok) return;

        setAuthToken(data.access_token);
        if (data.refresh_token) setRefreshToken(data.refresh_token);
        setUser((prev) => ({
          ...prev,
          email: data.email,
          name: data.name,
          isLoggedIn: true,
        }));
        setStep("main");
      } catch {
        // silent — user can login manually
      }
    };

    window.addEventListener("message", handleSsoMessage);
    return () => window.removeEventListener("message", handleSsoMessage);
  }, []);

  useEffect(() => {
    const handleThemeMessage = (event) => {
      if (event.data?.type !== "THEME_CHANGE") return;
      setIsDark(event.data.theme !== "light");
    };
    window.addEventListener("message", handleThemeMessage);
    return () => window.removeEventListener("message", handleThemeMessage);
  }, []);

  useEffect(() => {
    if (selectedCrypto.code === "BTC") {
      setWalletAddress(""); // 🔥 clear ETH / old bc1
    }
  }, [selectedCrypto]);

  useEffect(() => {
    const init = async () => {
      await waitForInit;
      const params = new URLSearchParams(window.location.search);
    const urlTheme = params.get("theme");

    const stored = await secureGet(STORAGE_KEY);
    const token = getAuthToken();

    if (stored) {
      try {
        const parsed = JSON.parse(stored);

        // Restore step
        if (parsed.step === "chat" && !parsed.chatMerchant) {
          setStep("main");
        } else if (parsed.step === "processing" || parsed.step === "success") {
          setStep("success");
          setTxHash(parsed.txHash || null);
        } else {
          setStep(parsed.step);
        }

        setMode(parsed.mode);
        setFiatAmount(parsed.fiatAmount);
        if (parsed.selectedCryptoCode !== "BTC") {
          setWalletAddress(parsed.walletAddress);
        }

        if (parsed.chatMerchant) {
          setChatMerchant(parsed.chatMerchant);
        }

        // 🔥 Only mark logged in if token exists
        setUser({
          ...parsed.user,
          isLoggedIn: !!token,
        });

        pendingCurrencyCodeRef.current = parsed.selectedCurrencyCode;

        const paymentMethodId = parsed.selectedPaymentMethodId;
        let paymentMethod;

        if (paymentMethodId?.startsWith("GLOBAL_")) {
          // If it was a global method, it will be refetched by the MainForm 
          // or we can store the whole object. For simplicity, we'll let it be restored 
          // but we need to ensure the ID is kept.
          paymentMethod = parsed.selectedPaymentMethod;
        } else {
          paymentMethod = paymentMethods.find((p) => p.id === paymentMethodId);
        }

        if (paymentMethod) setSelectedPaymentMethod(paymentMethod);

        const crypto = cryptoCurrencies.find(
          (c) => c.code === parsed.selectedCryptoCode
        );
        if (crypto) setSelectedCrypto(crypto);


        if (urlTheme === "light") {
          setIsDark(false);
        } else if (urlTheme === "dark") {
          setIsDark(true);
        } else {
          setIsDark(parsed.isDark ?? true);
        }
      } catch (e) {
        console.error("Failed to parse stored state:", e);
      }
    } else {
      setIsDark(urlTheme === "light" ? false : true);

      // 🔥 If no widget state but token exists → restore login
      if (token) {
        setUser((prev) => ({
          ...prev,
          isLoggedIn: true,
        }));
      }
    }

    setIsInitialized(true);
    };
    init();
  }, []);

  useEffect(() => {
    if (!isInitialized) return;

    const stateToStore = {
      step,
      mode,
      fiatAmount,
      selectedCurrencyCode: selectedCurrency.code,
      selectedPaymentMethodId: selectedPaymentMethod.id,
      selectedPaymentMethod: selectedPaymentMethod,
      selectedCryptoCode: selectedCrypto.code,

      walletAddress,
      user,
      isDark,
      txHash,
      chatMerchant,
    };
    secureSet(STORAGE_KEY, JSON.stringify(stateToStore));
  }, [
    step,
    mode,
    fiatAmount,
    selectedCurrency,
    selectedPaymentMethod,
    selectedCrypto,
    walletAddress,
    user,
    isDark,
    txHash,
    chatMerchant,
    isInitialized,
  ]);

  useEffect(() => {
    if (isDark) {
      document.documentElement.classList.add("dark");
    } else {
      document.documentElement.classList.remove("dark");
    }
  }, [isDark]);

  const calculatedCryptoAmount = calculateCryptoAmount(
    parseFloat(fiatAmount) || 0,
    selectedCrypto.price
  );

  const handleMainSubmit = () => {
    if (!user.isLoggedIn) {
      setStep("auth");
    } else {
      setStep("wallet");
    }
  };

  // if (showSplash) {
  //   return (
  //     <div className="min-h-screen flex items-center justify-center">
  //       <div className="w-full max-w-md">
  //         <SplashScreen onComplete={() => setShowSplash(false)} />
  //       </div>
  //     </div>
  //   );
  // }

  const handleLogin = (email, isExistingUser, data) => {
    setUser({
      ...user,
      email,
      isLoggedIn: true,
      userId: data?.id || data?.userId,
      id: data?.id || data?.userId,

      kycStatus: data?.kycStatus || null,
      isApproved: data?.isApproved || false,
      kycCompleted: data?.kycCompleted || false,
      submittedKyc: data?.submittedKyc || false,

      isKycComplete: isExistingUser,
      dailyBuyLimit: data?.dailyBuyLimit || 0,
      dailySellLimit: data?.dailySellLimit || 0,
      dailyBuyMinLimit: data?.dailyBuyMinLimit || 0,
      dailySellMinLimit: data?.dailySellMinLimit || 0,
      minTradeAmount: data?.minTradeAmount || 0,
      escrowTimeout: data?.escrowTimeout || 0,
      data,
    });
    // Return to main screen after login/signup - user will proceed to wallet when they click buy/sell
    setStep("main");
  };

  const handleWalletSubmit = (address, merchantData) => {
    setWalletAddress(address);
    // Store merchant data from API if available
    if (merchantData) {
      setMerchant(merchantData);
    }
    // In sell mode, go to payment details step first
    if (mode === "sell") {
      setStep("paymentDetails");
    } else if (
      requireUserVideoKyc &&
      user?.kycStatus !== "APPROVED" &&
      user?.kycStatus !== "SUBMITTED"
    ) {
      setStep("kyc");
    } else {
      setStep("matching");
    }
  };

  const handlePaymentDetailsSubmit = (details) => {
    setPaymentDetails(details);
    if (
      requireUserVideoKyc &&
      user?.kycStatus !== "APPROVED" &&
      user?.kycStatus !== "SUBMITTED"
    ) {
      setStep("kyc");
    } else {
      setStep("matching");
    }
  };

  const handleKycComplete = () => {
    setUser({ ...user, isKycComplete: true });
    setStep("matching");
  };

  const handleMerchantFound = (foundMerchant) => {
    setMerchant(foundMerchant);
    setStep("payment");
  };

  const handleMerchantMatchingBack = (screen) => {
    setStep(screen);
    setPendingOrder(null);
  }

  const handlePaymentConfirmed = (blockchainTxHash, newDealId) => {
    if (blockchainTxHash) {
      setTxHash(blockchainTxHash);
    }

    setPendingOrder(null);

    if (blockchainTxHash == null || newDealId == null) {
      setStep("request");
      return;
    }

    // Sell mode skips "Verifying Payment" screen, goes straight to success
    if (mode === "sell") {
      setStep("success");
    } else {
      setStep("processing");
    }
  };

  const handleTransactionComplete = (hash) => {
    setTxHash(hash);
    setStep("success");
  };

  const handleNewTransaction = () => {
    setStep("main");
    setFiatAmount("10");
    setMerchant(null);
    setWalletAddress("");
    setTxHash(null);
    setDealId(null);
  };

  // Logout handler - clears auth token, disconnects wallet, resets state
  const handleLogout = async () => {
    // The guarded `logout` mutation needs a still-valid access token - if it's
    // already expired the call fails here, and without this fallback the
    // session row stays "active" in the admin panel for up to 7 more days
    // even though we're about to clear local state and log out.
    try { await logoutMutation(); } catch { await logoutCurrentSession(); }

    // Cross-app logout sync (Direction B: widget-p2p -> palzea.com). This
    // works the same whether the widget is standalone at widget-p2p.palzea.com
    // or embedded as an iframe inside p2p.palzea.com, since it's this same
    // widget code running either way. A fully invisible hidden iframe — no
    // popup, no visible window. This only works because palzea.com sets a
    // narrow SameSite=None signal cookie (separate from its real, Lax
    // session cookie) specifically so this cross-site iframe request can
    // identify which account to log out server-side. No email/token is
    // passed by us; the relay endpoint trusts only that cookie. Best-effort
    // — never blocks the widget's own logout below.
    try {
      const palzeaUrl = import.meta.env.VITE_PALZEA_MAIN_URL || "https://palzea.com";
      const iframe = document.createElement("iframe");
      iframe.style.display = "none";
      iframe.src = `${palzeaUrl}/p2p/logout-relay`;
      document.body.appendChild(iframe);
      setTimeout(() => { try { iframe.remove(); } catch (e) { /* noop */ } }, 1200);
    } catch (e) { /* best-effort */ }

    const socket = getChatSocket();
    if (socket) {
      socket.removeAllListeners();
      socket.disconnect();
    }

    disconnectPresenceSocket();
    clearAuthToken();
    disconnect(); // Disconnect wallet

    setUser({
      email: "",
      isLoggedIn: false,
      isKycComplete: false,
      walletAddress: "",
      dailyBuyLimit: 0,
      dailySellLimit: 0,
      escrowTimeout: 0
    });

    setStep("main");
    setFiatAmount("10");
    setCryptoAmount("");
    setMerchant(null);
    setWalletAddress("");
    setTxHash(null);
    setDealId(null);
    setPaymentDetails(null);

    localStorage.removeItem(STORAGE_KEY);
  };

  if (!isInitialized) {
    return null;
  }

  return (
    <main className="min-h-screen flex items-center justify-center">
      <div className="w-full max-w-[460px]">
        <div className="widget-container widget-enter relative h-[620px] overflow-hidden">
          <div className="relative z-10 h-[calc(100%-30px)] flex flex-col overflow-y-auto hide-scrollbar">
            <Suspense fallback={
              <div className="flex-1 flex items-center justify-center h-full">
                <div className="h-8 w-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
              </div>
            }>
            {step === "main" && (
              <MainForm
                mode={mode}
                setMode={setMode}
                fiatAmount={fiatAmount}
                setFiatAmount={setFiatAmount}
                cryptoAmount={cryptoAmount}
                setCryptoAmount={setCryptoAmount}
                selectedCurrency={selectedCurrency}
                setSelectedCurrency={setSelectedCurrency}
                selectedPaymentMethod={selectedPaymentMethod}
                setSelectedPaymentMethod={setSelectedPaymentMethod}
                selectedCrypto={selectedCrypto}
                setSelectedCrypto={setSelectedCrypto}
                onSubmit={handleMainSubmit}
                onNavigate={setStep}
                onLogout={handleLogout}
                isLoggedIn={user.isLoggedIn}
                isDark={isDark}
                userLimits={{
                  dailyBuyLimit: user.dailyBuyLimit,
                  dailySellLimit: user.dailySellLimit,
                  dailyBuyMinLimit: user.dailyBuyMinLimit,
                  dailySellMinLimit: user.dailySellMinLimit,
                  minTradeAmount: user.minTradeAmount,
                }}
              />
            )}

            {step === "profile" && (
              <ProfileScreen
                onBack={() => setStep("main")}
                user={user}
                onOpenKyc={() => {
                  setUser((prev) => ({
                    ...prev,
                    fromProfile: true,
                  }));

                  setStep("kyc");
                }}
                onOpenSecurity={() => setStep("security")}
              />
            )}

            {step === "security" && (
              <SecurityScreen onBack={() => setStep("profile")} />
            )}

            {step === "limits" && (
              <LimitsScreen onBack={() => setStep("main")} />
            )}

            {step === "orders" && (
              <OrdersScreen
                isStagingPartner={isStagingPartner}
                onBack={() => setStep("main")}
                onOpenTransaction={(order) => {

                  // =====================================
                  // RESTORE ORDER
                  // =====================================

                  setPendingOrder(order);

                  // =====================================
                  // RESTORE MODE
                  // =====================================

                  if (
                    order.type?.toLowerCase() === "buy"
                  ) {
                    setMode("buy");
                  } else {
                    setMode("sell");
                  }

                  // =====================================
                  // RESTORE CRYPTO
                  // =====================================

                  const crypto =
                    cryptoCurrencies.find(
                      (c) =>
                        c.code === order.crypto
                    );

                  if (crypto) {
                    setSelectedCrypto(crypto);
                  }

                  // =====================================
                  // RESTORE AMOUNTS
                  // =====================================

                  setFiatAmount(
                    String(order.fiat || "")
                  );

                  setCryptoAmount(
                    String(order.amount || "")
                  );

                  // =====================================
                  // OPEN MATCHING SCREEN
                  // =====================================

                  setStep("matching");
                }}
              />
            )}

            {step === "chats" && (
              <ChatList
                onBack={() => setStep("main")}
                onChatOpen={(merchant) => {
                  setChatMerchant(merchant);
                  setStep("chat");
                }}
              />
            )}

            {step === "chat" && chatMerchant && (
              <WidgetChat
                merchantId={chatMerchant?.merchantId || chatMerchant?.id}
                merchantName={chatMerchant?.name || chatMerchant?.fullName || t('widget.chat_list.default_merchant_name', 'Merchant')}
                userId={user.userId || user.id}
                isOnline={chatMerchant?.isOnline}
                isInitiallyOpen={true}
                onClose={() => {
                  setChatMerchant(null);
                  setStep("chats");
                }}
                inline={true}
              />
            )}

            {step === "auth" && (
              <AuthForm
                onBack={() => setStep("main")}
                onLogin={handleLogin}
                isNewUser={true}
              />
            )}

            {step === "wallet" && (
              <WalletEntry
                user={user}
                requireUserVideoKyc={requireUserVideoKyc}
                isStagingPartner={isStagingPartner}
                selectedCrypto={selectedCrypto}
                onBack={() => setStep("main")}
                onSubmit={handleWalletSubmit}
                onNewTransaction={handleNewTransaction}
                transactionData={{
                  mode,
                  fiatAmount: parseFloat(fiatAmount) || 0,
                  currencyCode: selectedCurrency.code,
                  cryptoAmount: parseFloat(cryptoAmount) || calculatedCryptoAmount,
                  cryptoCode: selectedCrypto.code,
                  paymentMethodId: selectedPaymentMethod.id,
                }}
              />
            )}

            {step === "kyc" && (
              <KYCVerification
                // onBack={() => setStep("wallet")}
                // onComplete={handleKycComplete}
                onBack={() => {
                  const cameFromProfile =
                    user?.fromProfile;

                  setUser((prev) => ({
                    ...prev,
                    fromProfile: false,
                  }));

                  setStep(
                    cameFromProfile
                      ? "profile"
                      : "wallet"
                  );
                }}
                onComplete={(data) => {

                  const cameFromProfile =
                    user?.fromProfile;

                  setUser((prev) => ({
                    ...prev,
                    fromProfile: false,
                  }));

                  // =====================================
                  // USER SKIPPED KYC
                  // =====================================

                  if (data?.skipped) {

                    setTimeout(() => {

                      setStep(
                        cameFromProfile
                          ? "profile"
                          : "matching"
                      );

                    }, 0);

                    return;
                  }

                  // =====================================
                  // KYC COMPLETED
                  // =====================================

                  if (cameFromProfile) {

                    // prevent handleKycComplete redirect
                    setTimeout(() => {
                      setStep("profile");
                    }, 0);

                  } else {

                    handleKycComplete();
                  }
                }}
              />
            )}

            {step === "paymentDetails" && (
              <PaymentDetailsForm
                selectedPaymentMethod={selectedPaymentMethod}
                onBack={() => setStep("wallet")}
                onSubmit={handlePaymentDetailsSubmit}
              />
            )}

            {step === "matching" && (
              <MerchantMatching
                isStagingPartner={isStagingPartner}
                fiatAmount={parseFloat(fiatAmount)}
                cryptoAmount={
                  parseFloat(cryptoAmount) || calculatedCryptoAmount
                }
                selectedCrypto={selectedCrypto}
                selectedCurrency={selectedCurrency}
                onMerchantFound={handleMerchantFound}
                onRetry={() => setStep("wallet")}
                onBack={handleMerchantMatchingBack}
                merchantData={merchant}
                onPaymentConfirmed={handlePaymentConfirmed}
                mode={mode}
                walletAddress={walletAddress}
                selectedPaymentMethod={selectedPaymentMethod}
                paymentDetails={paymentDetails}
                escrowTimeout={escrowTimeout || user.escrowTimeout}
                user={user}
                pendingOrder={pendingOrder}
                setStepState={setStep}
              />
            )}

            {(step == "request" || step === "processing" || step === "success") && (
              <TransactionStatus
                status={
                  step === "success"
                    ? "completed"
                    : step
                }
                fiatAmount={parseFloat(fiatAmount)}
                fiatCurrency={selectedCurrency}
                cryptoAmount={
                  parseFloat(cryptoAmount) || calculatedCryptoAmount
                }
                selectedCrypto={selectedCrypto}
                walletAddress={walletAddress}
                onNewTransaction={handleNewTransaction}
                onComplete={handleTransactionComplete}
                mode={mode}
                onNavigate={setStep}
                txHash={txHash}
                dealId={dealId}
                isStagingPartner={isStagingPartner}
              />
            )}
            </Suspense>
          </div>

          <div className="sticky bottom-0 p-1 z-20 bg-[hsl(var(--card))] border-t border-border dark:border-white/10">
            <TrustBadge />
          </div>

          {(merchant || chatMerchant) && step !== "chat" && (
            <WidgetChat
              merchantId={chatMerchant?.merchantId || chatMerchant?.id || merchant?.merchantId || merchant?.id}
              merchantName={chatMerchant?.name || chatMerchant?.merchantName || chatMerchant?.fullName || merchant?.name || merchant?.merchantName || merchant?.fullName}
              userId={user.userId || user.id}
              isOnline={chatMerchant?.isOnline || merchant?.isOnline}
              isInitiallyOpen={!!chatMerchant}
              onClose={() => {
                setChatMerchant(null);
                if (step === "chat") setStep("chats");
              }}
            />
          )}
        </div>
      </div>
    </main>
  );
};
